import { EventEmitter } from '@angular/core';
import { DisplayDecorator, DisplayDecoratorFromJson, JsonDisplayDecorator } from '../../decorators/display-decorator';
import { ODataModel } from '../../interfaces/odata-model.interface';
import { JsonRadioModelOptions, RadioModelOptions, RadioModelOptionsFromJson } from '../options/radio-model-options';
import { ArrayModel, ODataArrayOutModel } from './array.model';

export class RadioModel<T extends ODataModel> extends ArrayModel<DisplayDecorator<T>> {
    public value: Array<DisplayDecorator<T>>;
    public selectedEntry: DisplayDecorator<T>;
    public newEntrySelected$ = new EventEmitter<DisplayDecorator<T>>();
    public constructor(
        name: string,
        value: Array<DisplayDecorator<T>>,
        public options = new RadioModelOptions(),
    ) {
        super(name, value, options);

        if (options.initialSelection >= -1) {
            this.selectByIndex(options.initialSelection);
        }
    }

    /**
     * Iterate over internal models array and call outModel conversion function for each of the models
     *
     * @returns {object} the outModel representation of this array model
     */
    public getOutModel(): ODataRadioOutModel {
        const outModel: ODataRadioOutModel = {};
        outModel[this.name] = this.selectedEntry ? this.selectedEntry.getOutModel()[this.selectedEntry.name] : null;
        return outModel;
    }

    /**
     * Get value of model inside selected DisplayDecorator
     */
    public getSelectedValue(): T['value'] {
        return this.selectedEntry?.value;
    }

    /**
     * Get selected index
     */
    public getSelectedIndex(): number {
        return this.value.findIndex((entry) => entry === this.selectedEntry);
    }

    /**
     * Select an entry in the dropdown
     *
     * @param index
     * @returns {boolean} true if the selection could be done, false otherwise
     */
    public selectByIndex(index: number): boolean {
        // if we initially allowed the radio group to have no selected option, then we should also allow to de-select
        // by setting the index to -1 again.
        const lowerBound = this.options.initialSelection < 0 ? -1 : 0;

        if (index >= this.value.length || index < lowerBound) {
            // $log.warn("Cannot select entry with index " + index + ": Only " + this.entries.length + " entries available.");
            return false;
        }

        this.selectedEntry = this.value[index];
        this.newEntrySelected$.next(this.value[index]);
        return true;
    }

    public selectByDisplayName(displayName: string): boolean {
        const index = this.value.findIndex((entry) => entry.displayName === displayName);
        return this.selectByIndex(index);
    }

    public selectByPredicate(predicate: (entry: DisplayDecorator<T>) => boolean): boolean {
        const index = this.value.findIndex(predicate);

        return this.selectByIndex(index);
    }

    public toJSON(): Partial<RadioModel<T>> {
        return {
            name: this.name,
            value: this.value,
            options: this.options,
            selectedEntry: this.selectedEntry,
        };
    }
}

export class RadioModelFromJson<T extends ODataModel> extends RadioModel<T> {
    public constructor(type: new (...args: unknown[]) => T, data: JsonRadioModel) {
        super(
            data.name,
            data.value.map((e) => new DisplayDecoratorFromJson(type, e)),
            new RadioModelOptionsFromJson(data.options),
        );

        // restore selection (if single-select was enabled)
        if (data.selectedEntry) {
            const selectedEntryIndex = data.value.findIndex(
                (entry) => data.selectedEntry.model.value === entry.model.value,
            );
            this.selectByIndex(selectedEntryIndex);
        }
    }
}

export interface JsonRadioModel {
    name: string;
    value: JsonDisplayDecorator[];
    options: JsonRadioModelOptions;
    selectedEntry: JsonDisplayDecorator;
}

// need here type because empty interfaces are not allowed
export type ODataRadioOutModel = ODataArrayOutModel;
